export type ReportTypeCategory =
  | "Cardiology"
  | "Pathology / Laboratory"
  | "Imaging"
  | "Other";

export type ReportTypeOption = {
  value: string;
  label: string;
  keywords?: string[];
  category: ReportTypeCategory;
};

export type GroupedReportTypeOptions = {
  category: ReportTypeCategory;
  options: ReportTypeOption[];
};

export const REPORT_TYPE_CATEGORY_ORDER: readonly ReportTypeCategory[] = [
  "Cardiology",
  "Pathology / Laboratory",
  "Imaging",
  "Other",
];

export const OTHER_REPORT_TYPE = "other";

/** Report types that support pairwise AI comparison. */
export const COMPARABLE_REPORT_TYPES = ["chart"] as const;

export type ComparableReportType = (typeof COMPARABLE_REPORT_TYPES)[number];

export const isComparableReportType = (
  reportType: string
): reportType is ComparableReportType =>
  (COMPARABLE_REPORT_TYPES as readonly string[]).includes(reportType);

export const REPORT_TYPE_OPTIONS: ReportTypeOption[] = [
  {
    value: "chart",
    label: "Super ECG",
    category: "Cardiology",
    keywords: ["super", "ecg", "chart"],
  },
  {
    value: "ecg",
    label: "ECG",
    category: "Cardiology",
    keywords: ["ec", "ecg", "electro"],
  },
  {
    value: "echo",
    label: "Echo",
    category: "Cardiology",
    keywords: ["echo", "echocardiogram"],
  },
  {
    value: "stress_test_tmt",
    label: "Stress Test / TMT",
    category: "Cardiology",
    keywords: ["stress", "tmt", "treadmill"],
  },
  {
    value: "holter_monitoring",
    label: "Holter Monitoring",
    category: "Cardiology",
    keywords: ["holter", "monitor"],
  },
  {
    value: "cardiac_ct",
    label: "Cardiac CT",
    category: "Cardiology",
    keywords: ["cardiac", "ct"],
  },
  {
    value: "angiography_report",
    label: "Angiography Report",
    category: "Cardiology",
    keywords: ["angio", "angiography"],
  },
  {
    value: "cbc",
    label: "CBC",
    category: "Pathology / Laboratory",
    keywords: ["cbc", "blood", "count"],
  },
  {
    value: "hba1c",
    label: "HbA1c",
    category: "Pathology / Laboratory",
    keywords: ["hba1c", "a1c", "glycated"],
  },
  {
    value: "lipid_profile",
    label: "Lipid Profile",
    category: "Pathology / Laboratory",
    keywords: ["lip", "lipid", "cholesterol", "profile"],
  },
  {
    value: "lft",
    label: "LFT",
    category: "Pathology / Laboratory",
    keywords: ["lft", "liver"],
  },
  {
    value: "kft",
    label: "KFT",
    category: "Pathology / Laboratory",
    keywords: ["kft", "kidney", "renal"],
  },
  {
    value: "thyroid_profile",
    label: "Thyroid Profile",
    category: "Pathology / Laboratory",
    keywords: ["thyroid", "tsh"],
  },
  {
    value: "blood_sugar_fasting",
    label: "Blood Sugar Fasting",
    category: "Pathology / Laboratory",
    keywords: ["fasting", "fbs", "glucose", "sugar"],
  },
  {
    value: "blood_sugar_pp",
    label: "Blood Sugar PP",
    category: "Pathology / Laboratory",
    keywords: ["pp", "post", "prandial", "glucose", "sugar"],
  },
  {
    value: "creatinine",
    label: "Creatinine",
    category: "Pathology / Laboratory",
    keywords: ["creatinine", "creat"],
  },
  {
    value: "trop_i",
    label: "Troponin",
    category: "Pathology / Laboratory",
    keywords: ["trop", "troponin"],
  },
  {
    value: "d_dimer",
    label: "D-Dimer",
    category: "Pathology / Laboratory",
    keywords: ["dimer", "d-dimer", "ddimer"],
  },
  {
    value: "crp",
    label: "CRP",
    category: "Pathology / Laboratory",
    keywords: ["crp", "c-reactive"],
  },
  {
    value: "vitamin_d",
    label: "Vitamin D",
    category: "Pathology / Laboratory",
    keywords: ["vitamin", "d3", "d"],
  },
  {
    value: "vitamin_b12",
    label: "Vitamin B12",
    category: "Pathology / Laboratory",
    keywords: ["b12", "vitamin"],
  },
  {
    value: "urine_routine",
    label: "Urine Routine",
    category: "Pathology / Laboratory",
    keywords: ["urine", "routine"],
  },
  {
    value: "chest_xray",
    label: "Chest X-Ray",
    category: "Imaging",
    keywords: ["chest", "xray", "x-ray", "radiograph"],
  },
  {
    value: "ct_scan",
    label: "CT Scan",
    category: "Imaging",
    keywords: ["ct", "scan", "tomography"],
  },
  {
    value: "mri",
    label: "MRI",
    category: "Imaging",
    keywords: ["mri", "magnetic"],
  },
  {
    value: "ultrasound",
    label: "Ultrasound",
    category: "Imaging",
    keywords: ["ultrasound", "sono", "usg"],
  },
  {
    value: OTHER_REPORT_TYPE,
    label: "Other",
    category: "Other",
    keywords: ["other", "custom"],
  },
];

const REPORT_TYPE_BY_VALUE = new Map(
  REPORT_TYPE_OPTIONS.map((opt) => [opt.value, opt])
);

/** Returns the display label for a report type slug, if known. */
export function getReportTypeLabel(value: string): string | undefined {
  return REPORT_TYPE_BY_VALUE.get(value)?.label;
}

/** Filters report type options by partial match on label and keywords. */
export function filterReportTypeOptions(query: string): ReportTypeOption[] {
  const trimmed = query.trim();
  if (!trimmed) {
    return REPORT_TYPE_OPTIONS;
  }
  const q = trimmed.toLowerCase();
  return REPORT_TYPE_OPTIONS.filter((opt) => {
    if (opt.label.toLowerCase().includes(q)) {
      return true;
    }
    return opt.keywords?.some((kw) => kw.toLowerCase().includes(q)) ?? false;
  });
}

/** Groups options under category headers in catalog order. */
export function groupReportTypeOptions(
  options: ReportTypeOption[]
): GroupedReportTypeOptions[] {
  const byCategory = new Map<ReportTypeCategory, ReportTypeOption[]>();
  for (const opt of options) {
    const list = byCategory.get(opt.category) ?? [];
    list.push(opt);
    byCategory.set(opt.category, list);
  }
  const grouped: GroupedReportTypeOptions[] = [];
  for (const category of REPORT_TYPE_CATEGORY_ORDER) {
    const categoryOptions = byCategory.get(category);
    if (categoryOptions && categoryOptions.length > 0) {
      grouped.push({ category, options: categoryOptions });
    }
  }
  return grouped;
}
